import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/common";
import { toast } from "sonner";
import { Star, ExternalLink, Plus, Newspaper, MapPin } from "lucide-react";
import { NuanceThread } from "@/components/common";

const LEVELS = [["", "All jurisdictions"], ["city", "City"], ["region", "Region"], ["country", "Country"], ["eu", "European Union"]];

export default function News() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [news, setNews] = useState([]);
  const [areas, setAreas] = useState([]);
  const [fLevel, setFLevel] = useState("");
  const [fValue, setFValue] = useState("");
  const [fTopic, setFTopic] = useState("");
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", source_url: "", jurisdiction_level: "city", jurisdiction_value: "", topics: "" });

  const biasLabel = (v) => {
    if (v == null) return t("Not rated");
    if (v < -1.5) return t("Leans left");
    if (v > 1.5) return t("Leans right");
    return t("Centre");
  };

  const load = () => {
    const p = new URLSearchParams();
    if (fLevel) p.set("jurisdiction_level", fLevel);
    if (fValue) p.set("jurisdiction_value", fValue);
    if (fTopic) p.set("topic", fTopic);
    api.get(`/news?${p.toString()}`).then((r) => setNews(r.data));
  };
  useEffect(() => { load(); }, [fLevel, fValue, fTopic]);
  useEffect(() => { api.get("/policy-areas").then((r) => setAreas(r.data.areas)); }, []);

  const rate = async (id, trust, bias) => {
    try { await api.post(`/news/${id}/rate`, { trust, bias }); toast.success(t("Rating saved")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const publish = async (e) => {
    e.preventDefault();
    try {
      await api.post("/news", { ...form, topics: form.topics.split(",").map((x) => x.trim()).filter(Boolean) });
      toast.success(t("Article published")); setShow(false); setForm({ title: "", body: "", source_url: "", jurisdiction_level: "city", jurisdiction_value: "", topics: "" }); load();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };
  const addNuance = async (id, text, sources) => {
    try { await api.post(`/news/${id}/nuance`, { text, sources }); toast.success(t("Nuance added")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); throw e; }
  };

  return (
    <div className="space-y-6" data-testid="news-page">
      <div className="flex items-end justify-between border-b-2 border-foreground pb-3">
        <div><p className="overline mb-1">{t("Verified journalism · platform-wide")}</p><h1 className="font-display text-3xl font-extrabold tracking-tight">{t("News & Media")}</h1></div>
        {user.user_type === "news_organisation" && (
          <button data-testid="news-new-btn" onClick={() => setShow(!show)} className="flex items-center gap-1.5 bg-primary text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-primary/90 transition-colors"><Plus size={16} /> {t("Publish")}</button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3" data-testid="news-filters">
        <select value={fLevel} onChange={(e) => { setFLevel(e.target.value); }} className={ic + " w-auto"} data-testid="news-filter-level">{LEVELS.map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}</select>
        <input placeholder={t("Jurisdiction (e.g. Bologna, Italy)")} value={fValue} onChange={(e) => setFValue(e.target.value)} className={ic + " w-auto"} data-testid="news-filter-value" />
        <select value={fTopic} onChange={(e) => setFTopic(e.target.value)} className={ic + " w-auto"} data-testid="news-filter-topic"><option value="">{t("All topics")}</option>{areas.map((a) => <option key={a} value={a}>{t(a)}</option>)}</select>
        {(fLevel || fValue || fTopic) && <button onClick={() => { setFLevel(""); setFValue(""); setFTopic(""); }} className="text-xs text-primary font-semibold">{t("Clear")}</button>}
      </div>

      {show && (
        <form onSubmit={publish} className="border border-border bg-white p-6 space-y-3" data-testid="news-form">
          <input required placeholder={t("Headline")} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={ic} />
          <textarea required placeholder={t("Article body")} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={ic + " h-28"} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <select value={form.jurisdiction_level} onChange={(e) => setForm({ ...form, jurisdiction_level: e.target.value })} className={ic} data-testid="news-jur-level"><option value="city">{t("City")}</option><option value="region">{t("Region")}</option><option value="country">{t("Country")}</option><option value="eu">{t("European Union")}</option></select>
            <input placeholder={t("Jurisdiction value (e.g. Bologna)")} value={form.jurisdiction_value} onChange={(e) => setForm({ ...form, jurisdiction_value: e.target.value })} className={ic} data-testid="news-jur-value" />
            <input placeholder={t("Topics (comma)")} value={form.topics} onChange={(e) => setForm({ ...form, topics: e.target.value })} className={ic} />
            <input placeholder={t("Source URL")} value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} className={ic} />
          </div>
          <button data-testid="news-publish-submit" className="bg-primary text-white px-4 py-2 rounded-sm text-sm font-semibold">{t("Publish article")}</button>
        </form>
      )}

      <div className="space-y-4">
        {news.map((n) => (
          <article key={n.id} className="border border-border bg-white p-8 hover:shadow-md transition-all" data-testid="news-item">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="text-xs font-semibold text-secondary flex items-center gap-1"><Newspaper size={13} /> {n.author_name}</span>
              <Badge tone="purple"><span className="inline-flex items-center gap-1"><MapPin size={10} /> {n.jurisdiction_value || n.jurisdiction_level}</span></Badge>
              {(n.topics || []).map((tp) => <Badge key={tp}>{t(tp)}</Badge>)}
            </div>
            <h2 className="font-display text-2xl font-bold tracking-tight mb-2">{n.title}</h2>
            <p className="text-muted-foreground leading-relaxed">{n.body}</p>
            {n.source_url && <a href={n.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-primary font-medium mt-3">{t("Source")} <ExternalLink size={14} /></a>}
            <div className="mt-5 pt-4 border-t border-border flex flex-wrap items-center gap-6">
              <div className="text-sm"><span className="text-muted-foreground">{t("Trust:")} </span><strong>{n.avg_trust ?? "—"}/5</strong></div>
              <div className="text-sm"><span className="text-muted-foreground">{t("Bias:")} </span><strong>{biasLabel(n.avg_bias)}</strong> <span className="text-xs text-muted-foreground">({n.ratings_count})</span></div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-5">
              <div className="flex items-center gap-1" data-testid={`news-trust-${n.id}`}>
                <span className="text-xs text-muted-foreground mr-1">{t("Rate trust:")}</span>
                {[1, 2, 3, 4, 5].map((s) => <button key={s} onClick={() => rate(n.id, s, Math.round(n.avg_bias ?? 0))} className="text-amber-500 hover:scale-110 transition-transform"><Star size={16} fill="currentColor" /></button>)}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{t("Bias:")}</span>
                <input type="range" min={-5} max={5} defaultValue={Math.round(n.avg_bias ?? 0)} onMouseUp={(e) => rate(n.id, Math.round(n.avg_trust ?? 3), parseInt(e.target.value))} data-testid={`news-bias-${n.id}`} className="accent-secondary" />
                <span className="text-[10px] text-muted-foreground">{t("left ◄ ► right")}</span>
              </div>
            </div>
            <NuanceThread nuances={n.nuances} onAdd={(text, sources) => addNuance(n.id, text, sources)} testid={`news-nuance-${n.id}`} />
          </article>
        ))}
        {news.length === 0 && <p className="text-sm text-muted-foreground">{t("No articles found for this filter.")}</p>}
      </div>
    </div>
  );
}
const ic = "w-full border border-border bg-background rounded-sm px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary";
